import { createFileRoute } from '@tanstack/react-router'
import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'

import { useTRPC, type RouterOutputs } from '@/trpc'

import $type from '@tandem/ui/type.css'
import $table from '@tandem/ui/table.css'
import $anim from '@tandem/ui/anim.css'
import $badge from '@tandem/ui/badge.css'
import $layout from '@tandem/ui/layout.css'
import $modal from '@tandem/ui/Modal/styles.css'
import Spinner from '@tandem/ui/Spinner'
import Modal from '@tandem/ui/Modal'

import XMarkIcon from '@tandem/ui/icons/xmark.svg?react'
import CheckmarkIcon from '@tandem/ui/icons/checkmark.svg?react'
import $ from './-styles.css'
import EditUserForm from '@/admin/EditUserForm'
import TrashIcon from '@tandem/ui/icons/trash.svg?react'
import TandemIcon from '@tandem/ui/icons/tandem.svg?react'
import TruckBoxFillIcon from '@tandem/ui/icons/truck.box.fill.svg?react'
import StorefrontFillIcon from '@tandem/ui/icons/storefront.fill.svg?react'

import { UserPlusIcon } from 'lucide-react'
import CreateUserForm from '@/admin/CreateUserForm'

type User = RouterOutputs['user']['list'][number]

export const Route = createFileRoute('/(admin)/users/')({
  component: UsersPage,
})

function UsersPage() {
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let [showModal, setShowModal] = useState(false)
  let [activeUser, setActiveUser] = useState<User | null>(null)
  let [search, setSearch] = useState('')

  let { data: users = [], isFetching } = useQuery(trpc.user.list.queryOptions({ search: search || undefined }))

  let deleteUser = useMutation({
    ...trpc.user.delete.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.user.list.queryFilter())
    }
  })

  let closeModal = useCallback(() => {
    setShowModal(false)
    setActiveUser(null)
  }, [])

  let openEdit = useCallback((user: User) => {
    setActiveUser(user)
    setShowModal(true)
  }, [])

  return (
    <div class={clsx('container', $.container)}>
      <h3>Пользователи</h3>
      <header class={$.header}>
        <div class={$.search}>
          <input type='text' placeholder='Найти' value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div class={$.controls}>
          <button class={$.create} onClick={() => setShowModal(true)}>
            <UserPlusIcon />
            Создать
          </button>
        </div>
      </header>

      {users.length > 0 ?
        <table class={clsx('unstyled', $table.table, { [$anim.loadingOverlay]: isFetching })}>
          <thead>
            <tr>
              <th>Имя</th>
              <th>Роль</th>
              <th>Телефон</th>
              <th>Эл. почта</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              let isSystemAdmin = user.companies.some((c) => c.id === 0 && c.role === 'admin')
              return (
              <tr key={user.id}>
                <td>
                  <button onClick={() => openEdit(user)} class='link'>
                    {user.first_name} {user.last_name}
                  </button>
                </td>

                <td style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25em' }}>
                  {user.companies.map((c) => (
                    <span key={c.id} class={clsx($badge.pill, c.id === 0 && $badge.system)}>
                      <span>{roleLabel(c.role)}</span>
                      <span>{c.id === 0 ? <TandemIcon /> : <>{c.type === 'distributor' ? <TruckBoxFillIcon /> : <StorefrontFillIcon />} {c.name}</>}</span>
                    </span>
                  ))}
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                    {user.phones.map((phone) => (
                      <div key={phone.id}>{phone.number}</div>
                    ))}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25em' }}>
                    {user.emails.map((email) => (
                      <div key={email.id} class={clsx($layout.withIcon, $layout.compact)}>
                        {email.is_verified && <CheckmarkIcon class={clsx($layout.icon)} />}
                        {email.address}
                      </div>
                    ))}
                  </div>
                </td>
                <td>
                  <div class={$.actions}>
                    {!isSystemAdmin && (
                      <button
                        class={clsx('plain', $type.muted)}
                        onClick={() => {
                          if (confirm(`Удалить пользователя "${user.first_name} ${user.last_name}"?`)) {
                            deleteUser.mutate({ id: user.id })
                          }
                        }}
                        disabled={deleteUser.isPending}
                      >
                        {deleteUser.isPending && deleteUser.data === user.id ?
                          <Spinner />
                        : <TrashIcon />}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              )
            })}
          </tbody>
        </table>
      : <p class={$type.muted}>
          {isFetching ?
            <Spinner />
          : 'Нет пользователей.'}
        </p>
      }

      <Modal open={showModal} onClose={closeModal}>
        <div class={$modal.header}>
          <h2>
            {activeUser ?
              <>
                {activeUser.first_name} {activeUser.last_name}
              </>
            : 'Новый пользователь'}
          </h2>
          <button class={clsx('plain', $modal.close)} onClick={closeModal} aria-label='Close'>
            <XMarkIcon />
          </button>
        </div>
        {activeUser ?
          <EditUserForm user={activeUser} onCancel={closeModal} />
        : <CreateUserForm onCancel={closeModal} onSave={closeModal} />}
      </Modal>
    </div>
  )
}

function roleLabel(role: string) {
  switch (role) {
    case 'admin': return 'Админ'
    case 'manager': return 'Менеджер'
    default: return role
  }
}
